import {
  AutomaticCheckInIcon,
  Button,
  ButtonLink,
  Dialog,
  EditIcon,
  EllipsisVerticalIcon,
  SendIcon,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendInvoice } from '../../../../api/payments/payments';
import { InvoiceStatus, getWSFeeListItemDescription, hasParentPaidInvoice } from '../../../../helpers/invoices';
import { USER_STATUS } from '../../../../helpers/userStatus';
import { currencyFormatter, formatFullName, getFormatter } from '../../../../helpers/utils';
import { InvoiceStatusBadge } from '../../../Billing/Invoices';
import AlertMessage, { Alert } from '../../../Shared/AppAlert';
import { InvoiceDetailSummarySection } from './InvoiceDetailSummarySection';
import { DetailCard, getResponsibleForBilling } from './InvoiceDetailUtils';
import { InvoiceEditDialog, invoiceCanEdit } from './InvoiceEditDialog';
import { InvoiceRefundDialog, invoiceCanRefund } from './InvoiceRefundDialog';
import { InvoiceStatusDialog } from './InvoiceStatusDialog';

export function InvoiceDetailSummary({ organization, invoice, student, invoiceType, onInvoiceUpdate }) {
  const organizationId = organization.id;
  const { t } = useTranslation();
  const { isSelfServeRefundsEnabled, paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer } = useFlags();
  const [selectedInvoice, setSelectedInvoice] = useState(invoice);
  const hasInvoiceBeenPaid = hasParentPaidInvoice(invoice?.payments);
  const wasAlreadySent = Boolean(selectedInvoice?.sentAt);
  const [isSending, setIsSending] = useState(false);
  const [invoiceSentAlert, setInvoiceSentAlert] = useState<Alert | null>(null);
  const responsibleForBilling = getResponsibleForBilling(student);

  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const invoiceRows =
    selectedInvoice &&
    selectedInvoice.invoiceItemList &&
    selectedInvoice.invoiceItemList.reduce((acc, item, index) => {
      // Determine if the item should be displayed
      let displayItem = shouldDisplayItem(item);

      // If the item is not to be displayed, skip it
      if (!displayItem) return acc;

      // Add the main item row
      acc.push(
        <tr key={`row-display-${index}`} className="align-middle">
          <td data-testid={`invoice-detail-category-${index}`}>
            {item.processingFees
              ? t(
                  getWSFeeListItemDescription(
                    item.category.startsWith('ACH') ? { feeType: 'ach_fee' } : { feeType: 'card_fee' }
                  ),
                  {
                    descriptionValue: item.processingFees.description,
                    cardFeePercentage: item.processingFees.cardFeePercentage,
                    cardFixedFee: item.processingFees.cardFixedFee,
                    achFeePercentage: item.processingFees.achFeePercentage,
                    achMaxFee: item.processingFees.achMaxFee,
                  }
                )
              : item.category}{' '}
          </td>
          <td data-testid={`invoice-detail-item-${index}`}>{item.item}</td>
          <td data-testid={`invoice-detail-notes-${index}`}>{item.notes}</td>
          <td data-testid={`invoice-detail-amount-${index}`} className="text-right">
            {currencyFormatter(item.amount, { precision: 2 })}
          </td>
        </tr>
      );

      // Add the discount rows, if any. `discounts` is treated as optional
      // everywhere else it's rendered (see InvoiceItemList / InvoiceItemEditor /
      // Widgets), so guard it here too rather than crash the whole detail view.
      item.discounts?.forEach((discount, j) => {
        acc.push(
          <tr key={`d-${index}-${j}`} className="align-middle">
            <td data-testid={`invoice-detail-category-${index}-discount-${j}`}>{discount.discountType}</td>
            <td />
            <td />
            <td data-testid={`invoice-detail-amount-${index}-discount-${j}`} className="text-right">
              {'(-' + getFormatter(discount.amountType)(discount.amount) + ')'}
            </td>
          </tr>
        );
      });

      return acc;
    }, []);

  const family = selectedInvoice && selectedInvoice.student && selectedInvoice.student.family;
  let billedTo: string;

  if (selectedInvoice.billedTo) {
    billedTo = selectedInvoice.billedTo.displayName;
  } else if (responsibleForBilling) {
    billedTo = responsibleForBilling.displayName;
  } else if (family && Object.keys(family).length > 0) {
    billedTo = Object.keys(family)
      .filter((fam) => !!family[fam].email)
      .map((fam) => {
        // check if fullName exists
        const fullName = formatFullName(family[fam], true);
        return fullName === '' ? null : fullName;
      })[0] as string;
  } else {
    billedTo = t('billing.invoiceDetails.recipients.emptyMessage');
  }

  function renderPageTitle(selectedInvoice) {
    const showActionsButton =
      selectedInvoice &&
      !(selectedInvoice.status === InvoiceStatus.OPEN && selectedInvoice.userStatus === USER_STATUS.PAID) &&
      !(selectedInvoice.status === InvoiceStatus.OPEN && selectedInvoice.userStatus === USER_STATUS.PROCESSING) &&
      !(selectedInvoice.status === InvoiceStatus.OPEN && selectedInvoice.userStatus === USER_STATUS.TRANSFERRING) &&
      !selectedInvoice.alreadyPaid &&
      !selectedInvoice.manuallyPaid &&
      ![InvoiceStatus.PROCESSING, InvoiceStatus.VOID, InvoiceStatus.PAID].includes(selectedInvoice.status);

    return (
      <div className="mb-2 flex items-center justify-between">
        <span data-testid="invoice-detail-status">
          <InvoiceStatusBadge invoice={selectedInvoice} withTooltip />
        </span>
        <div className="ml-auto flex items-center">
          {invoiceCanRefund(selectedInvoice, { isSelfServeRefundsEnabled }) ? (
            <ButtonLink
              content={t('billing.invoice.startRefundLink')}
              extraClasses="pl-8 pr-0"
              icon={<AutomaticCheckInIcon className="size-4 flex-none" />}
              linkButtonSize={WidgetSizeEnum.SMALL}
              onClick={() => {
                setShowRefundModal(true);
              }}
            />
          ) : null}
          {shouldRenderResendLink() ? (
            <ButtonLink
              data-testid="invoice-detail-resend-button"
              content={t('billing.invoice.resendInvoice')}
              extraClasses="pl-8 pr-0"
              icon={<SendIcon className="size-4 flex-none" />}
              linkButtonSize={WidgetSizeEnum.SMALL}
              onClick={() => {
                setInvoiceSentAlert(null);
                setShowSendModal(true);
              }}
            />
          ) : null}
          {invoiceCanEdit(selectedInvoice) ? (
            <ButtonLink
              data-testid="invoice-detail-edit-button"
              content={t('billing.invoice.editInvoice')}
              extraClasses="pl-8 pr-0"
              icon={<EditIcon className="size-4 flex-none" />}
              linkButtonSize={WidgetSizeEnum.SMALL}
              onClick={() => {
                setInvoiceSentAlert(null);
                setShowEditModal(true);
              }}
            />
          ) : null}
          {showActionsButton && (
            <ButtonLink
              data-testid="invoice-detail-actions-button"
              content={t('common.more')}
              extraClasses="pl-8 pr-0"
              icon={<EllipsisVerticalIcon className="size-4 flex-none" />}
              iconDirection="right"
              linkButtonSize={WidgetSizeEnum.SMALL}
              onClick={() => {
                setInvoiceSentAlert(null);
                setShowActionsModal(true);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  const renderConfirmSendModal = () => (
    <div className="confirm-send-modal">
      {wasAlreadySent ? (
        <span>
          {t('billing.invoice.confirmResend1')}
          <b>{t('billing.invoice.confirmResend2')}</b>
          {t('billing.invoice.confirmResend3')}
        </span>
      ) : (
        <span>{t('billing.invoice.confirmSend')}</span>
      )}
      <Button primary extraClasses={'mt=6'} loading={isSending} disabled={isSending} onClick={onSendInvoice}>
        {t('billing.invoiceDetails.resend.confirmAction')}
      </Button>
    </div>
  );

  const handleOnSendFailed = (error?: unknown) => {
    if (error) console.error(error);

    setInvoiceSentAlert({
      type: 'error',
      message: 'Something went wrong, please try again',
    });

    setIsSending(false);
  };

  const onSendInvoice = async () => {
    setIsSending(true);
    setShowSendModal(false);

    let updatedFields;

    try {
      updatedFields = await sendInvoice(selectedInvoice.id, wasAlreadySent);
      if (!updatedFields) {
        return handleOnSendFailed();
      }
    } catch (error) {
      return handleOnSendFailed(error);
    }

    setInvoiceSentAlert({
      type: 'success',
      message: 'billing.invoice.sent',
    });
    setSelectedInvoice({ ...selectedInvoice, ...updatedFields });
    setIsSending(false);
  };

  // Show a one-click "Resend invoice" link for an invoice that was already sent
  // to the parent but isn't paid/processing/void — the common "parent didn't get
  // the email" case that previously forced a void+recreate. Reuses the existing
  // send flow (onSendInvoice -> sendInvoice(id, wasAlreadySent:true)), which
  // resends the email only and does NOT re-open/charge the invoice.
  const shouldRenderResendLink = (): boolean => {
    return (
      wasAlreadySent &&
      !hasInvoiceBeenPaid &&
      !selectedInvoice?.alreadyPaid &&
      !selectedInvoice?.manuallyPaid &&
      selectedInvoice?.userStatus !== USER_STATUS.PAID &&
      selectedInvoice?.userStatus !== USER_STATUS.PROCESSING &&
      selectedInvoice?.userStatus !== USER_STATUS.TRANSFERRING &&
      ![InvoiceStatus.PROCESSING, InvoiceStatus.VOID, InvoiceStatus.PAID].includes(selectedInvoice?.status)
    );
  };

  function shouldDisplayItem(item) {
    const isProcessingFeeLineItem = item.processingFees !== undefined;
    if (paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer && isProcessingFeeLineItem) {
      return false;
    }

    return true;
  }

  return (
    <DetailCard title={renderPageTitle(selectedInvoice)}>
      <InvoiceStatusDialog
        invoice={selectedInvoice}
        invoiceType={invoiceType}
        isOpen={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        onInvoiceUpdate={onInvoiceUpdate}
        onSuccess={(updatedData) => setSelectedInvoice((prev) => ({ ...prev, ...updatedData }))}
      />
      <Dialog isOpen={showSendModal} onClose={() => setShowSendModal(false)}>
        <Dialog.Panel>{renderConfirmSendModal()}</Dialog.Panel>
      </Dialog>
      <InvoiceEditDialog
        invoice={selectedInvoice}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onInvoiceUpdate={onInvoiceUpdate}
        onSuccess={(updatedData) => setSelectedInvoice((prev) => ({ ...prev, ...updatedData }))}
      />
      <AlertMessage alert={invoiceSentAlert} setAlert={setInvoiceSentAlert} />
      <InvoiceRefundDialog
        invoice={selectedInvoice}
        organizationId={organizationId}
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onRefunded={(updatedData) => setSelectedInvoice((prev) => ({ ...prev, ...updatedData }))}
      />

      <InvoiceDetailSummarySection
        billedTo={billedTo}
        selectedInvoice={selectedInvoice}
        invoiceRows={invoiceRows}
        hasInvoiceBeenPaid={hasInvoiceBeenPaid}
      />
    </DetailCard>
  );
}
